import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Lock, ArrowRight, Crown, Sparkles } from 'lucide-react';

/**
 * Feature-to-minimum-plan mapping.
 * 'free' = all plans, 'starter' = starter+, 'pro' = pro only
 */
const FEATURE_PLAN_MAP = {
  '/analyzer': 'free',
  '/comps': 'free',
  '/repair-estimator': 'free',
  '/chat': 'starter',
  '/projects': 'free',
  '/funding': 'pro',
  '/marketing': 'pro',
  '/wholesale': 'pro',
  '/analytics': 'pro',
  '/crm': 'starter',
  '/compare': 'starter',
  '/contracts': 'starter',
  '/contractors': 'starter',
  '/esign': 'starter',
  '/directory': 'free',
  '/pitch-deck': 'pro',
  '/leaderboard': 'starter',
  '/teams': 'pro',
  '/referrals': 'free',
};

const PLAN_HIERARCHY = { free: 0, starter: 1, pro: 2 };

const PLAN_LABELS = {
  starter: { name: 'Starter', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  pro: { name: 'Pro', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30' },
};

function canAccessFeature(userPlan, requiredPlan) {
  return (PLAN_HIERARCHY[userPlan] || 0) >= (PLAN_HIERARCHY[requiredPlan] || 0);
}

function getUserPlan(user) {
  if (!user) return 'free';
  // Admin users always get pro-level access
  if (user.isAdmin) return 'pro';
  const role = user.role || 'user';
  if (role === 'admin' || role === 'super_admin') return 'pro';
  const sub = user.subscription;
  if (!sub) return 'free';
  // If plan is expired or cancelled, treat as free
  if (sub.status === 'expired' || sub.status === 'cancelled') return 'free';
  return sub.plan || 'free';
}

export default function PlanGate({ path, featureName, children }) {
  const { user, isAuthenticated } = useAuth();

  // Not logged in — let other guards handle it
  if (!isAuthenticated || !user) return children;

  const requiredPlan = FEATURE_PLAN_MAP[path];
  // If path isn't gated or is free, pass through
  if (!requiredPlan || requiredPlan === 'free') return children;

  const userPlan = getUserPlan(user);
  if (canAccessFeature(userPlan, requiredPlan)) return children;

  const planInfo = PLAN_LABELS[requiredPlan] || PLAN_LABELS.pro;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-20 flex items-center justify-center px-4" data-testid="plan-gate-upgrade">
      <div className="max-w-md w-full text-center">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-8">
          <div className={`w-16 h-16 ${planInfo.bg} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
            <Lock className={`w-7 h-7 ${planInfo.color}`} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Upgrade Your Plan
          </h1>

          <p className="text-gray-500 dark:text-gray-400 mb-2">
            <span className="font-semibold text-gray-700 dark:text-gray-200">{featureName || 'This feature'}</span> requires the{' '}
            <span className={`font-bold ${planInfo.color}`}>{planInfo.name}</span> plan or higher.
          </p>

          <p className="text-sm text-gray-400 dark:text-gray-500 mb-8">
            You're currently on the <span className="font-medium capitalize">{userPlan}</span> plan.
          </p>

          <div className="space-y-3">
            <Link to="/pricing" className="block">
              <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-lg" data-testid="upgrade-plan-btn">
                <Crown className="w-4 h-4 mr-2" />
                View Plans & Upgrade
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/billing" className="block">
              <Button variant="outline" className="w-full" data-testid="manage-billing-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Manage Billing
              </Button>
            </Link>
          </div>
        </div>

        <p className="text-xs text-gray-400 dark:text-gray-600 mt-6">
          Need help choosing a plan?{' '}
          <Link to="/contact" className="text-purple-600 dark:text-purple-400 hover:underline">Contact us</Link>
        </p>
      </div>
    </div>
  );
}

export { FEATURE_PLAN_MAP, canAccessFeature, getUserPlan };
